import React from 'react';
import { motion } from 'framer-motion';
import { 
  DollarSign, 
  Users, 
  Briefcase, 
  Target,
  CheckCircle,
  AlertCircle,
  Info,
  Star,
  Zap
} from 'lucide-react';
import { useGameStore } from '../store/gameStore';

const NotificationCenter: React.FC = () => {
  const { notifications } = useGameStore();

  const getNotificationIcon = (type: string) => {
    const icons = {
      event: Info,
      habit: Target,
      decision: CheckCircle,
      money: DollarSign,
      achievement: Star,
      milestone: Zap,
    };
    return icons[type as keyof typeof icons] || Info;
  };

  const getNotificationColor = (type: string) => {
    const colors = {
      event: 'text-blue-400 bg-blue-500/20 border-blue-500/30',
      habit: 'text-emerald-400 bg-emerald-500/20 border-emerald-500/30',
      decision: 'text-purple-400 bg-purple-500/20 border-purple-500/30',
      money: 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30',
      achievement: 'text-orange-400 bg-orange-500/20 border-orange-500/30',
      milestone: 'text-indigo-400 bg-indigo-500/20 border-indigo-500/30',
    };
    return colors[type as keyof typeof colors] || 'text-slate-400 bg-slate-500/20 border-slate-500/30';
  };

  const formatTimestamp = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  return (
    <div className="space-y-3 max-h-64 overflow-y-auto">
      {notifications.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 bg-slate-700/50 rounded-full flex items-center justify-center mx-auto mb-3">
            <Info size={24} className="text-slate-400" />
          </div>
          <p className="text-slate-500 text-sm">No notifications yet</p>
        </div>
      ) : (
        notifications
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, 8) // Show only the 8 most recent notifications
          .map((notification, index) => {
            const Icon = getNotificationIcon(notification.type);
            const colorClass = getNotificationColor(notification.type);
            
            return (
              <motion.div
                key={notification.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className={`flex items-start space-x-3 p-3 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-colors ${
                  !notification.read ? 'border-l-4 border-blue-500' : ''
                }`}
              >
                <div className={`p-2 rounded-lg border ${colorClass} flex-shrink-0`}>
                  <Icon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-medium text-white truncate">
                      {notification.title}
                    </h4>
                    {!notification.read && (
                      <div className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0 ml-2" />
                    )}
                  </div>
                  <p className="text-xs text-slate-300 mb-1 line-clamp-2">
                    {notification.message}
                  </p>
                  <p className="text-xs text-slate-500">
                    {formatTimestamp(notification.timestamp)}
                  </p>
                </div>
              </motion.div>
            );
          })
      )}
    </div>
  );
};

export default NotificationCenter;